import React, { useEffect, useRef, useState } from 'react';
import { FaRobot, FaTimes, FaRedo, FaCommentDots, FaArrowLeft, FaChevronDown, FaChevronRight } from 'react-icons/fa';
import { useCarriereEvents } from 'contexts/CarriereEventsContext';
import './CarriereChatbot.css';

const CarriereChatbot = () => {
  const {
    messages,
    chatbotVisible,
    isInModal,
    categories,
    toggleChatbot,
    resetMessages,
    handleQuestionClick,
  } = useCarriereEvents();

  const messagesEndRef = useRef(null);
  const [showCategories, setShowCategories] = useState(true);
  const [expandedCategories, setExpandedCategories] = useState({});

  // Initialize all categories as expanded by default
  useEffect(() => {
    if (categories && Object.keys(expandedCategories).length === 0) {
      const initialExpanded = {};
      categories.forEach(cat => {
        initialExpanded[cat.id] = true;
      });
      setExpandedCategories(initialExpanded);
    }
  }, [categories, expandedCategories]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (chatbotVisible && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, chatbotVisible]);

  // Check if we should show categories (only show after welcome message if last message is welcome)
  useEffect(() => {
    if (messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      setShowCategories(lastMessage.context === 'BIENVENUE');
    }
  }, [messages]);

  const handleQuestionClickWrapper = (questionId, questionText) => {
    handleQuestionClick(questionId, questionText);
    setShowCategories(false);
  };

  const handleBackToCategories = () => {
    setShowCategories(true);
  };

  const handleResetMessages = () => {
    resetMessages();
    setShowCategories(true);
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  // Format timestamp
  const formatTimestamp = (date) => {
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  // Get context icon
  const getContextIcon = (context) => {
    switch (context) {
      case 'RIS':
        return '📄';
      case 'PERIODE':
        return '📅';
      case 'PARAM':
        return '⚙️';
      case 'BIENVENUE':
        return '👋';
      case 'QUESTION':
        return '❓';
      case 'REPONSE':
        return '💡';
      default:
        return '💬';
    }
  };

  return (
    <>
      {/* Floating Button */}
      {isInModal && !chatbotVisible && (
        <button
          className="carriere-chatbot-floating-button"
          onClick={toggleChatbot}
          title="Ouvrir l'assistant"
        >
          <FaRobot />
        </button>
      )}

      {/* Chatbot Panel */}
      <div className={`carriere-chatbot-panel ${chatbotVisible ? 'visible' : 'hidden'}`}>
        {/* Header */}
        <div className="carriere-chatbot-header">
          <div className="carriere-chatbot-header-title">
            <FaRobot />
            <h3>Alan</h3>
          </div>
          <div className="carriere-chatbot-header-actions">
            {!showCategories && messages.length > 1 && (
              <button
                className="carriere-chatbot-header-button"
                onClick={handleBackToCategories}
                title="Retour aux catégories"
              >
                <FaArrowLeft />
              </button>
            )}
            <button
              className="carriere-chatbot-header-button"
              onClick={handleResetMessages}
              title="Réinitialiser"
            >
              <FaRedo />
            </button>
            <button
              className="carriere-chatbot-header-button"
              onClick={toggleChatbot}
              title="Fermer"
            >
              <FaTimes />
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="carriere-chatbot-messages">
          {messages.length === 0 ? (
            <div className="carriere-chatbot-empty">
              <FaCommentDots />
              <p>Aucun message pour le moment</p>
            </div>
          ) : (
            messages.map((message, index) => {
              // If showing categories, only show welcome message
              if (showCategories && message.context !== 'BIENVENUE') {
                return null;
              }
              
              // If not showing categories, apply filtering logic
              if (!showCategories) {
                // Always show system events (RIS, PERIODE, PARAM)
                const isSystemEvent = ['RIS', 'PERIODE', 'PARAM'].includes(message.context);
                
                if (isSystemEvent) {
                  // System events are always shown
                } else if (message.context === 'QUESTION' || message.context === 'REPONSE') {
                  // For questions/answers, only show the last question and answer pair
                  const lastQuestionIndex = messages.map((m, i) => m.context === 'QUESTION' ? i : -1)
                    .filter(i => i !== -1)
                    .pop();
                  
                  if (index < lastQuestionIndex) {
                    return null;
                  }
                } else {
                  // Hide any other message types when not showing categories
                  return null;
                }
              }
              
              return (
                <div key={message.id}>
                  <div
                    className={`carriere-chatbot-message ${message.type}`}
                  >
                    <div className="carriere-chatbot-message-content">
                      <div className="carriere-chatbot-message-header">
                        <span className={`carriere-chatbot-message-context ${message.context}`}>
                          {getContextIcon(message.context)} {message.context=="PARAM"?"PARAMETRES":message.context}
                        </span>
                        <span className="carriere-chatbot-message-timestamp">
                          {formatTimestamp(message.timestamp)}
                        </span>
                      </div>
                      <p 
                        className="carriere-chatbot-message-text"
                        dangerouslySetInnerHTML={{ __html: message.text }}
                      />
                    </div>
                  </div>
                  
                  {/* Show back button after answer */}
                  {message.context === 'REPONSE' && !showCategories && (
                    <div className="carriere-chatbot-back-to-start">
                      <button 
                        className="carriere-chatbot-back-button"
                        onClick={handleBackToCategories}
                      >
                        <FaArrowLeft />
                        Retour au menu
                      </button>
                    </div>
                  )}
                  
                  {/* Show categories after the welcome message */}
                  {message.context === 'BIENVENUE' && showCategories && categories && (
                    <div className="carriere-chatbot-categories">
                      {categories.map(category => (
                        <div key={category.id} className={`carriere-chatbot-category carriere-chatbot-category-${category.id}`}>
                          <div 
                            className="carriere-chatbot-category-header"
                            onClick={() => toggleCategory(category.id)}
                          >
                            <div className="carriere-chatbot-category-title-wrapper">
                              <h4>{category.title}</h4>
                              {category.description && (
                                <p className="carriere-chatbot-category-description">
                                  {category.description}
                                </p>
                              )}
                            </div>
                            <button 
                              className="carriere-chatbot-category-toggle"
                              aria-label={expandedCategories[category.id] ? "Réduire" : "Développer"}
                            >
                              {expandedCategories[category.id] ? <FaChevronDown /> : <FaChevronRight />}
                            </button>
                          </div>
                          {expandedCategories[category.id] && (
                            <div className="carriere-chatbot-questions">
                              {category.questions.map(question => (
                                <button
                                  key={question.id}
                                  className="carriere-chatbot-question-button"
                                  onClick={() => handleQuestionClickWrapper(question.id, question.text)}
                                >
                                  {question.text}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Footer */}
        <div className="carriere-chatbot-footer">
          <p className="carriere-chatbot-footer-text">
            Assistant virtuel • Guidage automatique
          </p>
        </div>
      </div>
    </>
  );
};

export default CarriereChatbot;
